import React, { useCallback, useEffect, useState } from 'react';
import CommonButton from '../../../Components/CommonButton';
import CommonLoader from '../../../Components/CommonLoader';
import NoDataPage from '../../../Components/NoDataPage';
import { useToast } from '../../../Components/CommonToast';
import { useConfirm } from '../../../Components/ConfirmModal';
import { heading3, bodySmall, Body } from '../../../Constants/Fonts';
import AddOnCard from '../../../Dashboard/AddOns/AddOnCard';
import { risk_portal_categories, risk_portal_delete_category } from '../../../services';
import CategoryForm from '../Forms/CategoryForm';

const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

// Subtle × delete control for a category tile — sits in the AddOnCard trailing
// slot. Hover tints it danger.
function DeleteButton({ name, onClick }) {
    const [hover, setHover] = useState(false);
    return (
        <button
            type="button"
            aria-label={`Delete ${name}`}
            title="Delete category"
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, flex: '0 0 auto', padding: 0, lineHeight: 1, fontSize: 15,
                borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                border: `1px solid ${hover ? 'var(--color-danger)' : 'var(--color-border)'}`,
                background: hover ? 'var(--color-danger-soft)' : 'var(--color-surface)',
                color: hover ? 'var(--color-danger)' : 'var(--color-text-muted)',
                transition: 'color .15s ease, border-color .15s ease, background .15s ease',
            }}
        >
            ×
        </button>
    );
}

function CategoryLabel({ name, description }) {
    return (
        <Body as="span" style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <Body as="span" weight="semibold" color="var(--color-text-primary)">{name}</Body>
            <Body
                as="span"
                variant={2}
                title={description || undefined}
                color="var(--color-text-secondary)"
                style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.45,
                }}
            >
                {description || '—'}
            </Body>
        </Body>
    );
}

// A 6-digit hex → a 10%-alpha soft tint for the AddOnCard icon chip; anything
// else falls back to a neutral token.
const softTint = (hex) => (/^#[0-9a-fA-F]{6}$/.test(hex || '') ? `${hex}1A` : 'var(--color-surface-muted)');

export default function Categories({ canManage = false }) {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();

    const load = useCallback(async () => {
        try {
            const d = unwrap(await risk_portal_categories());
            setCategories(Array.isArray(d) ? d : (Array.isArray(d?.categories) ? d.categories : []));
        } catch (e) {
            notify('Failed to load categories', 'error');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    const remove = async (c) => {
        const ok = await confirm({
            title: 'Delete category',
            message: `Delete "${c.name}"? Risks currently in this category will need re-categorising.`,
            confirmLabel: 'Delete', danger: true, themeColor: false,
        });
        if (!ok) return;
        setBusy(true);
        try {
            await risk_portal_delete_category(c.id);
            notify(`Category "${c.name}" deleted`, 'success');
            await load();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Failed to delete category', 'error');
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <CommonLoader fullPage label="Loading categories…" />;

    return (
        <div className='rp1_widget'>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
                <div>
                    <h2 style={heading3({ margin: 0 })}>Risk Categories</h2>
                    <p style={bodySmall({ color: 'var(--color-text-muted)', margin: '4px 0 0' })}>
                        Organise your risk taxonomy — every risk is classified into one category for reporting and appetite.
                    </p>
                </div>
                {canManage && <CommonButton text="Add Category" onClick={() => setShowForm(true)} />}
            </div>

            {categories.length === 0 ? (
                <NoDataPage
                    title="No categories yet"
                    hint="Add categories to organise your risk taxonomy (e.g. Cyber, Compliance, Operational)."
                />
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                    {categories.map((c) => {
                        const color = c.color || 'var(--color-text-muted)';
                        return (
                            <AddOnCard
                                key={c.id ?? c.name}
                                // top-align so the chip lines up with the name and the ×
                                // sits at the top-right; height:100% → equal-height tiles.
                                align="flex-start"
                                style={{ height: '100%' }}
                                iconNode={<Body as="span" style={{ width: 12, height: 12, borderRadius: '50%', background: color }} />}
                                iconBg={softTint(c.color)}
                                label={<CategoryLabel name={c.name} description={c.description} />}
                                // action (even null) keeps AddOnCard in custom mode so it
                                // never falls back to the "Enable" add-on affordance.
                                action={canManage && c.id != null ? <DeleteButton name={c.name} onClick={() => remove(c)} /> : null}
                            />
                        );
                    })}
                </div>
            )}

            {showForm && (
                <CategoryForm
                    show
                    onClose={() => setShowForm(false)}
                    onSuccess={(msg) => { notify(msg, 'success'); load(); }}
                    onError={(msg) => notify(msg, 'error')}
                />
            )}

            {busy && <CommonLoader fullPage label="Working…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}
